import {
  SignOutIcon,
  MagnifyingGlassIcon,
  SidebarSimpleIcon,
  ShieldCheckIcon,
  GearIcon,
  SlidersHorizontalIcon,
  CheckIcon,
  BuildingsIcon,
  ProhibitIcon,
} from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Sentry } from '~/sentry';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { InitialsAvatar } from '~/components/ui/avatar';
import { useInspector } from '~/lib/stores/ui-store';
import { useBackgroundTasks } from '~/hooks/queries/use-inspector';
import { useChatStatuses } from '~/hooks/queries/use-chat-status';
import { useApprovals } from '~/hooks/queries/use-approvals';
import { useActingOrgName, useUserDisplayName, useSwitchableTenants, useSwitchTenant } from '~/hooks/queries/use-tenant';
import { getInspectorTooltip } from '~/components/inspector-indicator';
import { resolvePendingApprovalsAffordance } from '~/components/pending-approvals-affordance';
import { useChatOpenStore } from '~/lib/stores/chat-open-store';
import { useOpenSettings, useOpenSystemSettings } from '~/features/settings/use-open-settings';
import { isHomeRoute } from '~/lib/chat-origin';

/**
 * The header's own pieces, rendered by `app-header.tsx`:
 *
 * - `ApprovalsBadge`: the pending-approvals badge and its Sentry dead-end report.
 * - `InspectorToggle`: the activity-aware Inspector button — amber with a
 *   pulsing dot while work runs.
 * - `AvatarMenu`: the avatar dropdown — User and System Settings, command
 *   palette, tenant switch, sign out.
 */

/** The pending-approvals badge — FRONT-1323's three cases, see `pending-approvals-affordance.ts`. */
export function ApprovalsBadge({ inspectorEnabled }: { inspectorEnabled: boolean }) {
  const { open: inspectorOpen, setOpen: setInspectorOpen } = useInspector();
  const navigate = useNavigate();
  const location = useLocation();
  const requestOpenChat = useChatOpenStore((s) => s.requestOpenChat);
  // `inspectorOpen` is PERSISTED (ui-store, FRONT-1187), so a browser that had
  // the panel open before the flag was switched off still reports open for a
  // panel that no longer mounts. Anything describing what the user can SEE has
  // to read this instead, or the badge styles itself as "panel is showing"
  // against an empty right-hand column.
  const inspectorPanelOpen = inspectorEnabled && inspectorOpen;

  const { data: approvals = [] } = useApprovals();
  // FRONT-1323: the badge stays visible regardless of the Inspector flag — it is
  // the only cross-page "something is waiting on you" signal, since AppHeader
  // mounts on every route while every other approvals surface is scoped to the
  // one chat currently open. Only its DESTINATION moves.
  const approvalsBadge = resolvePendingApprovalsAffordance(approvals, inspectorEnabled);

  // FRONT-1323: `deadEnd` is the carried dead-end — approvals are pending, the
  // Inspector is gated off, and not one of them has a chat to open, so the
  // badge can only report the count and disable itself. Every producer traced
  // says this should be unreachable now (see the matching note in
  // `approvals-service.ts`), but that is a claim about code, not a
  // measurement. Report it so the question gets settled by data.
  //
  // Keyed on the approval ids, not just the count: this must fire once per
  // distinct set, or it would re-report on every render and on every refetch
  // that returns the same rows.
  const reportedDeadEndKey = useRef<string | null>(null);
  useEffect(() => {
    // FRONT-2757: gate on `deadEnd`, not `target === "none"` — an empty list
    // also resolves to `none` and reported once per page load for every user.
    if (!approvalsBadge.deadEnd) {
      reportedDeadEndKey.current = null;
      return;
    }
    const key = approvals.map((a) => a.id).sort().join(',');
    if (reportedDeadEndKey.current === key) return;
    reportedDeadEndKey.current = key;
    Sentry.captureMessage('pending approvals with no answerable surface', {
      level: 'warning',
      tags: { ticket: 'FRONT-1323', inspector_enabled: String(inspectorEnabled) },
      extra: {
        count: approvals.length,
        approval_ids: approvals.map((a) => a.id),
        types: approvals.map((a) => a.type),
      },
    });
  }, [approvalsBadge.deadEnd, approvals, inspectorEnabled]);

  function openPendingApprovals() {
    if (approvalsBadge.target === 'inspector') {
      setInspectorOpen(true);
      return;
    }
    if (!approvalsBadge.chatOrigin) return;
    // Same one-modal-owner route the New chat button takes: navigate Home,
    // then let Home's single launcher open the thread.
    if (!isHomeRoute(location.pathname)) navigate('/');
    requestOpenChat(approvalsBadge.chatOrigin, null);
  }

  if (!approvalsBadge.visible) return null;
  return (
    <Button
      onClick={openPendingApprovals}
      disabled={approvalsBadge.target === 'none'}
      variant="ghost"
      size="sm"
      className={`h-8 gap-1.5 text-sm font-medium relative ${
        inspectorPanelOpen ? 'text-primary' : 'text-amber-600 dark:text-amber-400'
      }`}
      // FRONT-1273: explicit aria-label, not just the tooltip text — the
      // visible count alone reads as just a number to a screen reader.
      aria-label={approvalsBadge.hint}
      tooltip={approvalsBadge.hint}
    >
      <ShieldCheckIcon size={16} weight="fill" />
      {approvalsBadge.count}
      {!inspectorPanelOpen && (
        <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
      )}
    </Button>
  );
}

/**
 * The activity-aware Inspector button. The pulsing amber dot is the actual
 * signal ("background work happening, take a look"). It is the only way in
 * (FRONT-874), so it carries its own `aria-label`: the tooltip text is
 * dynamic (task counts) and can never serve as a stable accessible name.
 */
export function InspectorToggle() {
  const { open: inspectorOpen, toggle: toggleInspector } = useInspector();
  const { data: tasks } = useBackgroundTasks();
  const activeTaskCount = tasks?.counts.active ?? 0;
  // FRONT-661: the indicator also reads the chat-status spine, so a chat that is
  // working or needs you lights the dot — the same source the chat pills read.
  const { data: chatStatuses } = useChatStatuses();
  const chatsNeedAttention = chatStatuses
    ? Object.values(chatStatuses).some((s) => s.state === 'working' || s.state === 'needs-you')
    : false;
  // FRONT-662: a chat paused on a permission/question pulls the user back —
  // the quicker pull-back pulse instead of the calm working pulse.
  const chatsNeedYou = chatStatuses ? Object.values(chatStatuses).some((s) => s.state === 'needs-you') : false;
  const inspectorActive = activeTaskCount > 0 || chatsNeedAttention;

  const inspectorTooltip = getInspectorTooltip({ chatsNeedYou, inspectorActive, activeTaskCount });
  let inspectorIconColorClass: string;
  if (inspectorOpen) {
    inspectorIconColorClass = 'text-primary';
  } else if (inspectorActive) {
    inspectorIconColorClass = 'text-amber-600 dark:text-amber-400';
  } else {
    inspectorIconColorClass = 'text-muted-foreground hover:text-foreground';
  }

  return (
    <Button
      onClick={toggleInspector}
      variant="ghost"
      size="icon"
      // FRONT-2484: the Inspector is a side panel with no room beside a
      // phone-width page, so its toggle steps out of the crowded bar there.
      className={`h-8 w-8 relative max-sm:hidden ${inspectorIconColorClass}`}
      tooltip={inspectorTooltip}
      aria-label="Inspector"
      aria-pressed={inspectorOpen}
    >
      <SidebarSimpleIcon size={16} weight={inspectorActive ? 'fill' : 'regular'} />
      {inspectorActive && !inspectorOpen && (
        <span
          className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-amber-500 ${
            chatsNeedYou ? 'animate-pulse-fast' : 'animate-pulse'
          }`}
        />
      )}
    </Button>
  );
}

function openPalette() {
  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
}

function signOut() {
  fetch('/api/auth/logout', { method: 'POST' }).then(() => {
    window.location.href = '/login';
  });
}

/**
 * The menu's header: who you are, and (FRONT-2169) which organization you are
 * signed into. A person with an account in two orgs could not tell them apart
 * anywhere in the app. Reads the org name itself, so the menu cannot forget to
 * pass it; it mounts only while the menu is open, so the request is made on
 * open, not on every page. No name resolved → no line, never the raw id.
 */
export function AvatarMenuLabel({ userName, userEmail }: { userName: string; userEmail?: string }) {
  const orgName = useActingOrgName();
  return (
    <DropdownMenuLabel className="flex flex-col gap-0.5">
      <span className="text-sm font-medium [overflow-wrap:anywhere]">{userName}</span>
      <span className="text-[length:var(--text-2xs)] text-muted-foreground [overflow-wrap:anywhere]">
        {userEmail || 'Signed in'}
      </span>
      {orgName && (
        <span
          data-testid="avatar-menu-org"
          className="mt-1 flex items-center gap-1 text-[length:var(--text-2xs)] text-muted-foreground [overflow-wrap:anywhere]"
        >
          <BuildingsIcon size={12} aria-hidden className="shrink-0" />
          {orgName}
        </span>
      )}
    </DropdownMenuLabel>
  );
}

/** The avatar dropdown — identity, the two Settings doors, the palette, tenants, sign out. */
export function AvatarMenu() {
  const userName = useUserDisplayName();
  // FRONT-1357: tenant switcher. The backend answers an empty list while
  // grant-based tenancy is off (or the user has no cross-org grants), so the
  // menu section simply does not render in those cases.
  const { data: switchable } = useSwitchableTenants();
  const switchTenant = useSwitchTenant();
  const openSettings = useOpenSettings();
  const openSystemSettings = useOpenSystemSettings();
  const queryClient = useQueryClient();
  const userEmail = queryClient.getQueryData<{ email?: string }>(['auth', 'me'])?.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          // Round, per the handoff's avatar trigger (§5) — a person is the one
          // thing in the chrome that isn't a panel or a control.
          className="ml-1 p-0 overflow-hidden rounded-full"
          tooltip={userName}
          aria-label={userName}
        >
          <InitialsAvatar name={userName} size="md" tone="primary" className="h-full w-full rounded-full" />
        </Button>
      </DropdownMenuTrigger>
      {/* FRONT-2488: the menu grows to fit the address (up to 20rem) and a
          longer one wraps — a fixed w-52 cut ordinary emails mid-domain. */}
      <DropdownMenuContent align="end" className="w-auto min-w-52 max-w-[20rem]">
        <AvatarMenuLabel userName={userName} userEmail={userEmail} />
        <DropdownMenuSeparator />
        {/* FRONT-2376: "User Settings" beside "System Settings" — a bare
            "Settings" would be ambiguous about which of the two it opens. */}
        <DropdownMenuItem onSelect={() => openSettings()}>
          <GearIcon size={14} />
          User Settings
        </DropdownMenuItem>
        {/* FRONT-2376: the eight former sidebar pages — visible to every
            signed-in user; each section's own admin gate is enforced inside. */}
        <DropdownMenuItem onSelect={() => openSystemSettings()}>
          <SlidersHorizontalIcon size={14} />
          System Settings
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={openPalette}>
          <MagnifyingGlassIcon size={14} />
          Command palette
          <kbd className="ml-auto text-[length:var(--text-2xs)] text-muted-foreground">⌘K</kbd>
        </DropdownMenuItem>
        {switchable && switchable.tenants.length > 1 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[length:var(--text-2xs)] text-muted-foreground">Tenant</DropdownMenuLabel>
            {switchable.tenants.map((t) => {
              const isCurrent = t.orgId === switchable.current;
              return (
                <DropdownMenuItem
                  key={t.orgId}
                  disabled={isCurrent}
                  className={t.enabled ? undefined : 'opacity-60'}
                  onSelect={() => {
                    // Not-enabled orgs get an explanation instead of a doomed
                    // request (the backend would 403 anyway).
                    if (!t.enabled) {
                      toast.error(`EDDY is not enabled for ${t.label} yet.`);
                      return;
                    }
                    void switchTenant(t.orgId);
                  }}
                >
                  <BuildingsIcon size={14} />
                  <span className="truncate">{t.label}</span>
                  {isCurrent ? (
                    <CheckIcon size={12} className="ml-auto text-muted-foreground" />
                  ) : t.enabled ? null : (
                    <ProhibitIcon size={12} className="ml-auto text-muted-foreground" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={signOut} variant="destructive">
          <SignOutIcon size={14} />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
